import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { propertyAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { GlassCard } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Building2, MapPin, Search, ArrowLeft } from 'lucide-react';
import { Toaster } from 'sonner';

const PropertyListPublic = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      const res = await propertyAPI.getProperties(params);
      setProperties(res.data);
    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadProperties();
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="property-list-public">
      {/* Header */}
      <header className="border-b border-white/5 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Building2 className="w-8 h-8 text-emerald-500" />
              <span className="text-xl font-bold text-gradient-gold">SAM Rumah</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:text-emerald-400">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <Link to="/" className="text-slate-400 hover:text-white flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-white">Property Listings</h1>
          <p className="text-slate-400 mt-1">Discover your perfect property</p>
        </div>

        {/* Search */}
        <GlassCard className="p-4 mb-8">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                placeholder="Search by name or location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                data-testid="property-search"
              />
            </div>
            <Button onClick={handleSearch} className="bg-emerald-500 hover:bg-emerald-600">
              Search
            </Button>
          </div>
        </GlassCard>

        {/* Property Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : properties.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <Link key={property.id} to={`/login`} data-testid={`property-card-${property.id}`}>
                <GlassCard hover className="p-0 overflow-hidden h-full">
                  <div
                    className="h-48 bg-cover bg-center relative bg-slate-800"
                  >
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-emerald-500/80 text-white capitalize">
                        {property.property_type}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <p className="text-emerald-400 font-mono font-semibold">
                        {formatCurrency(property.price_range_min)} - {formatCurrency(property.price_range_max)}
                      </p>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-white mb-1">{property.name}</h3>
                    <div className="flex items-center gap-1 text-slate-400 text-sm mb-2">
                      <MapPin className="w-4 h-4" />
                      {property.location}
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2">{property.description}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                      <span className="text-xs text-slate-500">{property.total_units} units</span>
                      <span className="text-xs text-slate-500">by {property.developer}</span>
                    </div>
                  </div>
                </GlassCard>
              </Link>
            ))}
          </div>
        ) : (
          <GlassCard className="text-center py-12">
            <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Properties Found</h3>
            <p className="text-slate-400">Try adjusting your search filters</p>
          </GlassCard>
        )}
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
};

export default PropertyListPublic;
